import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { setupScrollAnimations } from '../utils/scrollAnimations';
import './style.css';

// Import project images
import MarbleProject1_1 from './images/marble/project1/Marble Project1 -1.png';
import MarbleProject1_2 from './images/marble/project1/Marble Project1 -2.png';
import MarbleProject1_3 from './images/marble/project1/Marble Project1 -3.png';
import MarbleProject1_4 from './images/marble/project1/Marble Project1 -4.png';

const Projects = () => {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState('all');
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);

  useEffect(() => {
    setupScrollAnimations();
    
    // Check for filter parameter in URL
    const urlParams = new URLSearchParams(window.location.search);
    const filterParam = urlParams.get('filter');
    if (filterParam && ['all', 'steel', 'marble', 'contracting'].includes(filterParam)) {
      setActiveFilter(filterParam);
    }
  }, []);

  // Project data - memoized to prevent re-creation on every render
  const projects = useMemo(() => [
    {
      id: 1,
      title: t('projects.project1.title'),
      category: 'marble',
      description: t('projects.project1.description'),
      year: '2024',
      location: 'Mathaba Hotel, Jeddah, Saudi Arabia',
      features: ['Premium Marble', 'Custom Design', 'Luxury Finish'],
      images: [MarbleProject1_1, MarbleProject1_2, MarbleProject1_3, MarbleProject1_4]
    },
    {
      id: 2,
      title: t('projects.project2.title'),
      category: 'steel',
      description: t('projects.project2.description'),
      year: '2024',
      location: 'Riyadh, Saudi Arabia',
      features: ['Structural Steel', 'Modern Design', 'High Durability'],
      images: ['/api/placeholder/400/300', '/api/placeholder/400/300']
    },
    {
      id: 3,
      title: t('projects.project3.title'),
      category: 'contracting',
      description: t('projects.project3.description'),
      year: '2023',
      location: 'Dammam, Saudi Arabia',
      features: ['Complete Construction', 'Project Management', 'On-time Delivery'],
      images: ['/api/placeholder/400/300', '/api/placeholder/400/300', '/api/placeholder/400/300']
    },
    {
      id: 4,
      title: t('projects.project4.title'),
      category: 'marble',
      description: t('projects.project4.description'),
      year: '2023',
      location: 'Mecca, Saudi Arabia',
      features: ['Boutique Design', 'Premium Materials', 'Elegant Finish'],
      images: ['/api/placeholder/400/300', '/api/placeholder/400/300']
    },
    {
      id: 5,
      title: t('projects.project5.title'),
      category: 'steel',
      description: t('projects.project5.description'),
      year: '2023',
      location: 'Jeddah, Saudi Arabia',
      features: ['Custom Design', 'Artistic Elements', 'Corporate Style'],
      images: ['/api/placeholder/400/300', '/api/placeholder/400/300', '/api/placeholder/400/300', '/api/placeholder/400/300']
    },
    {
      id: 6,
      title: t('projects.project6.title'),
      category: 'contracting',
      description: t('projects.project6.description'),
      year: '2022',
      location: 'Medina, Saudi Arabia',
      features: ['Multi-unit Complex', 'Modern Architecture', 'Sustainable Design'],
      images: ['/api/placeholder/400/300', '/api/placeholder/400/300']
    }
  ], [t]);

  // Filter projects based on active filter
  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter(project => project.category === activeFilter));
    }
  }, [activeFilter, projects]);

  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
  };

  const handleImageClick = (imageSrc) => {
    if (zoomedImage === imageSrc && isZoomed) {
      // If clicking the same image and it's already zoomed, close it
      setZoomedImage(null);
      setIsZoomed(false);
    } else if (zoomedImage === imageSrc && !isZoomed) {
      // If clicking the same image and it's not zoomed, zoom it
      setIsZoomed(true);
    } else {
      // If clicking a different image, open it
      setZoomedImage(imageSrc);
      setIsZoomed(false);
    }
  };

  const closeZoom = () => {
    setZoomedImage(null);
    setIsZoomed(false);
  };

  const getImageGridClass = (imageCount) => {
    if (imageCount === 1) return 'single-image';
    if (imageCount === 2) return 'two-images';
    if (imageCount === 3) return 'three-images';
    if (imageCount === 4) return 'four-images';
    return 'multiple-images';
  };

  return (
    <motion.main
      className="page-content"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
    >
      <div className="projects-content">
        {/* Hero Section */}
        <div className="projects-hero animate-on-scroll">
          <div className="projects-hero-content">
            <h1>{t('projects.title')}</h1>
            <p className="projects-hero-subtitle">
              {t('projects.subtitle')}
            </p>
          </div>
        </div>

        {/* Filter Section */}
        <div className="projects-filter-section animate-on-scroll">
          <div className="filter-buttons">
            <button 
              className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
              onClick={() => handleFilterClick('all')}
            >
              {t('projects.all')}
            </button>
            <button 
              className={`filter-btn ${activeFilter === 'steel' ? 'active' : ''}`}
              onClick={() => handleFilterClick('steel')}
            >
              {t('projects.steel')}
            </button>
            <button 
              className={`filter-btn ${activeFilter === 'marble' ? 'active' : ''}`}
              onClick={() => handleFilterClick('marble')}
            >
              {t('projects.marble')}
            </button>
            <button 
              className={`filter-btn ${activeFilter === 'contracting' ? 'active' : ''}`}
              onClick={() => handleFilterClick('contracting')}
            >
              {t('projects.contracting')}
            </button>
          </div>
        </div>

        {/* Projects Grid */}
        <div className="projects-grid animate-on-scroll">
          {filteredProjects.map((project, index) => (
            <motion.div
              key={project.id}
              className="project-card"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              {/* Project Images */}
              <div className={`project-images ${getImageGridClass(project.images.length)}`}>
                {project.images.map((image, imgIndex) => (
                  <div 
                    key={imgIndex} 
                    className="project-image-item"
                    onClick={() => handleImageClick(image)}
                  >
                    <img src={image} alt={`${project.title} ${imgIndex + 1}`} />
                    <div className="image-overlay">
                      <span className="zoom-icon">🔍</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Project Content */}
              <div className="project-content">
                <div className="project-header">
                  <h3>{project.title}</h3>
                  <span className="project-category">
                    {t(`projects.${project.category}`)}
                  </span>
                </div>
                
                <p className="project-description">{project.description}</p>
                
                <div className="project-details">
                  <div className="project-detail">
                    <span className="detail-icon">📅</span>
                    <span>{project.year}</span>
                  </div>
                  <div className="project-detail">
                    <span className="detail-icon">📍</span>
                    <span>{project.location}</span>
                  </div>
                </div>
                
                <div className="project-features">
                  {project.features.map((feature, featureIndex) => (
                    <span key={featureIndex} className="feature-tag">
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Image Zoom Modal */}
        {zoomedImage && (
          <div className="image-zoom-modal" onClick={closeZoom}>
            <div className="zoom-content" onClick={(e) => e.stopPropagation()}>
              <button className="close-zoom" onClick={closeZoom}>×</button>
              <img 
                src={zoomedImage} 
                alt="Zoomed project" 
                className={isZoomed ? 'zoomed-in' : 'normal-size'}
                onClick={() => setIsZoomed(!isZoomed)}
                style={{ cursor: 'pointer' }}
              />
            </div>
          </div>
        )}

        {/* Call to Action Section */}
        <div className="projects-cta animate-on-scroll">
          <div className="cta-content">
            <h3>Ready to Start Your Next Project?</h3>
            <p>Let us help you bring your vision to life with our expertise in steel, marble, and contracting services.</p>
            <div className="cta-buttons">
              <a href="/contact" className="btn btn-primary">
                Get a Quote
              </a>
              <a href="/contact" className="btn btn-secondary">
                Discuss Your Project
              </a>
            </div>
          </div>
        </div>
      </div>
    </motion.main>
  );
};

export default Projects;